import { Button, Skeleton } from '@parcel/ui';
import type * as React from 'react';
import type { ApiBrowseSummary } from '../../lib/browse-query.js';

interface ProjectBrowseSummary {
  total: number;
  new_count: number;
  as_of: string;
  map_version: string;
}

interface BrowseSummaryQuery {
  data: ApiBrowseSummary | ProjectBrowseSummary | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => unknown;
}

export function BrowseSummaryMeta({
  summaryQuery,
  baselineDataVersion,
  onRefresh,
}: {
  summaryQuery: BrowseSummaryQuery;
  baselineDataVersion: string | null;
  onRefresh: () => void;
}): React.JSX.Element {
  if (summaryQuery.isPending) {
    return (
      <span aria-label="Loading counts">
        <Skeleton className="inline-block h-4 w-24 align-middle" />
      </span>
    );
  }

  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <span className="inline-flex items-center gap-2">
        <span>Counts unavailable</span>
        <Button type="button" variant="outline" size="sm" onClick={() => summaryQuery.refetch()}>
          Retry
        </Button>
      </span>
    );
  }

  const dataVersion =
    'data_version' in summaryQuery.data
      ? summaryQuery.data.data_version
      : summaryQuery.data.map_version;
  const hasNewResults = baselineDataVersion !== null && dataVersion !== baselineDataVersion;

  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1">
      <span className="whitespace-nowrap">
        {summaryQuery.data.total.toLocaleString('en-US')} ·{' '}
        {summaryQuery.data.new_count.toLocaleString('en-US')} new
      </span>
      {hasNewResults ? (
        <button
          type="button"
          aria-label="Updates available; refresh view"
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-brand/25 bg-brand-muted px-2 py-0.5 text-xs font-medium leading-5 text-brand transition-colors hover:border-brand/40 hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <span className="size-1.5 rounded-full bg-brand" aria-hidden="true" />
          Refresh view
        </button>
      ) : null}
    </span>
  );
}
